import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import Loading from '@intelligence/components/Loading';
import { getImg } from '@/pages/home/_config/getImgs';
import type { IntelligenceReportDataInfo } from '../../types';
import type { TReportConfigMap } from '@intelligence/utils/reportConfig';
import {
  LATEST_INITIAL_BATCH,
  LATEST_PAGE_SIZE,
  POPULAR_INITIAL_BATCH,
  POPULAR_PAGE_SIZE,
} from '../../_constants';
import PopularReportItem from '../PopularReportItem';
import LatestReportCard from '../LatestReportCard';
import ScrollSentinel from '../ScrollSentinel';

interface IHomeContentProps {
  latestReports: IntelligenceReportDataInfo[];
  latestLoading: boolean;
  popularReports: IntelligenceReportDataInfo[];
  popularLoading: boolean;
  reportSourceConfig: TReportConfigMap;
  isEn: boolean;
  onLikeChange: (resourceId: string, likeCounts?: number) => void;
  onOpenReport: (report: IntelligenceReportDataInfo) => void;
}

const HomeContent: React.FC<IHomeContentProps> = ({
  latestReports,
  latestLoading,
  popularReports,
  popularLoading,
  reportSourceConfig,
  isEn,
  onLikeChange,
  onOpenReport,
}) => {
  const { formatMessage } = useIntl();

  /**
   * latest / popular 客户端分批渲染：
   * - 仅渲染前 visibleCount 条，控制首屏渲染开销（cover 图片请求量、Bubble/useMemo 挂载量）。
   * - 底部哨兵触底自动追加下一批（详见 ScrollSentinel）。
   * - 仅在语言切换时重置；数据源 in-place 字段更新（如点赞）不应重置滚动位置。
   */
  const [latestVisibleCount, setLatestVisibleCount] = useState<number>(LATEST_INITIAL_BATCH);
  const [popularVisibleCount, setPopularVisibleCount] = useState<number>(POPULAR_INITIAL_BATCH);

  useEffect(() => {
    setLatestVisibleCount(LATEST_INITIAL_BATCH);
    setPopularVisibleCount(POPULAR_INITIAL_BATCH);
  }, [isEn]);

  const visibleLatestReports = useMemo(
    () => latestReports.slice(0, latestVisibleCount),
    [latestReports, latestVisibleCount],
  );
  const visiblePopularReports = useMemo(
    () => popularReports.slice(0, popularVisibleCount),
    [popularReports, popularVisibleCount],
  );

  const hasMoreLatest = latestVisibleCount < latestReports.length;
  const hasMorePopular = popularVisibleCount < popularReports.length;

  // count 是哨兵建议的批次数；快速滚动时一次合并多批，避免逐批 setState 追不上视口。
  const handleLatestLoadMore = useCallback((count = 1): void => {
    setLatestVisibleCount(prev => prev + count * LATEST_PAGE_SIZE);
  }, []);
  const handlePopularLoadMore = useCallback((count = 1): void => {
    setPopularVisibleCount(prev => prev + count * POPULAR_PAGE_SIZE);
  }, []);

  if (latestLoading && popularLoading) {
    return (
      <div className="dbi-report-search__loading-container">
        <Loading />
      </div>
    );
  }

  const bothEmpty =
    !latestLoading &&
    !popularLoading &&
    latestReports.length === 0 &&
    popularReports.length === 0;

  if (bothEmpty) {
    return (
      <div className="dbi-report-search__empty-wrapper">
        <img
          src={getImg('no-data')}
          className="dbi-report-search__empty-img"
          alt=""
        />
        <div className="dbi-report-search__empty-title">
          {formatMessage({ id: 'dbi_report_search_empty_title' })}
        </div>
        <div className="dbi-report-search__empty-desc">
          {formatMessage({ id: 'dbi_report_search_empty_desc' })}
        </div>
      </div>
    );
  }

  let latestContent: React.ReactNode;
  if (latestLoading) {
    latestContent = <Loading classNameValue="dbi-report-search__loading" />;
  } else if (latestReports.length === 0) {
    latestContent = (
      <div className="dbi-report-search__section-empty">
        {formatMessage({ id: 'dbi_report_search_no_data' })}
      </div>
    );
  } else {
    latestContent = (
      <>
        <div className="dbi-report-search__latest-list">
          {visibleLatestReports.map(report => (
            <LatestReportCard
              key={report.resource_id}
              report={report}
              reportSourceConfig={reportSourceConfig}
              isEn={isEn}
              onLikeChange={onLikeChange}
            />
          ))}
        </div>
        <ScrollSentinel
          hasMore={hasMoreLatest}
          onLoadMore={handleLatestLoadMore}
        />
      </>
    );
  }

  let popularContent: React.ReactNode;
  if (popularLoading) {
    popularContent = <Loading classNameValue="dbi-report-search__loading" />;
  } else if (popularReports.length === 0) {
    popularContent = (
      <div className="dbi-report-search__section-empty">
        {formatMessage({ id: 'dbi_report_search_no_data' })}
      </div>
    );
  } else {
    popularContent = (
      <>
        {visiblePopularReports.map((report, idx) => (
          <PopularReportItem
            key={report.resource_id || `popular-${idx}`}
            rank={idx + 1}
            report={report}
            reportSourceConfig={reportSourceConfig}
            isEn={isEn}
            onClick={onOpenReport}
          />
        ))}
        <ScrollSentinel
          hasMore={hasMorePopular}
          onLoadMore={handlePopularLoadMore}
        />
      </>
    );
  }

  return (
    <div className="dbi-report-search__home">
      <section className="dbi-report-search__latest">
        <h2 className="dbi-report-search__section-title dbi-report-search__section-title--latest">
          <span
            className="dbi-report-search__section-icon"
            aria-hidden="true"
          />
          <span className="dbi-report-search__section-title-text">
            {formatMessage({ id: 'dbi_report_search_section_latest' })}
          </span>
        </h2>
        {latestContent}
      </section>

      <div className="dbi-report-search__popular">
        <h2 className="dbi-report-search__section-title dbi-report-search__section-title--popular">
          <span
            className="dbi-report-search__section-icon"
            aria-hidden="true"
          />
          <span className="dbi-report-search__section-title-text">
            {formatMessage({ id: 'dbi_report_search_section_popular' })}
          </span>
        </h2>
        <div className="dbi-report-search__popular-list">
          {popularContent}
        </div>
      </div>
    </div>
  );
};

export default React.memo(HomeContent);
